// Service-worker recovery utilities.
//
// Why this exists:
//   The PWA service worker can pin a tab to a stale JS bundle. If that
//   bundle has a bug fixed in a later deploy, the user white-screens on
//   load and a plain `location.reload()` doesn't help — Workbox re-serves
//   the same cached chunk. The fix has historically required DevTools
//   → Application → Clear site data, which operators can't be expected
//   to know.
//
//   These helpers give the ErrorBoundary a one-tap "Reset app" path
//   that:
//     1. Tries to activate any waiting SW (the normal update path).
//     2. Failing that, unregisters every SW for the origin AND wipes
//        every Cache Storage bucket.
//     3. Reloads — guarantees the user lands on the latest code.
//
// We avoid touching localStorage / IndexedDB / cookies so the user
// doesn't lose their session + offline-queue entries unless they
// explicitly opt into a deeper reset.

export async function activateWaitingServiceWorker(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false
  try {
    const reg = await navigator.serviceWorker.getRegistration()
    if (!reg) return false
    // Posting SKIP_WAITING is async fire-and-forget; the reload that callers
    // do next must navigate through the NEW SW, so wait for controllerchange
    // (activation usually lands <100ms). On timeout we still report true —
    // the message was sent and the reload is at worst the old no-op behavior,
    // which the callers' once-per-session guards already bound.
    const awaitTakeover = () => new Promise<void>((resolve) => {
      const timer = setTimeout(done, 4000)
      function done() {
        clearTimeout(timer)
        navigator.serviceWorker.removeEventListener('controllerchange', done)
        resolve()
      }
      navigator.serviceWorker.addEventListener('controllerchange', done)
    })
    const messageWaiting = async (sw: ServiceWorker | null | undefined): Promise<boolean> => {
      if (!sw) return false
      const takeover = awaitTakeover()
      sw.postMessage({ type: 'SKIP_WAITING' })
      await takeover
      return true
    }
    // If there's already a SW waiting, tell it to take over.
    if (await messageWaiting(reg.waiting)) return true
    // Otherwise probe for an update — if one's available, install it,
    // then activate.
    await reg.update()
    if (await messageWaiting(reg.waiting)) return true
    const installing = reg.installing
    if (installing) {
      await new Promise<void>((resolve) => {
        // 'redundant' = the install FAILED. Without it this promise never
        // settled, so chunkReload's reload never fired and the route hung on
        // the Suspense fallback forever (idle-wake audit B3 — the one real
        // stale-deploy hole). The timebox is belt-and-braces against any
        // other state-machine surprise; on timeout we fall through to the
        // messageWaiting check / return false, which callers treat as
        // "nothing safe to do" and reload/show the error UI.
        const timer = setTimeout(done, 10_000)
        function done() {
          clearTimeout(timer)
          installing!.removeEventListener('statechange', onState)
          resolve()
        }
        function onState() {
          const s = installing!.state
          if (s === 'installed' || s === 'activated' || s === 'redundant') done()
        }
        installing.addEventListener('statechange', onState)
      })
      if (await messageWaiting(reg.waiting)) return true
    }
    return false
  } catch {
    return false
  }
}

// True only if the network is genuinely reachable RIGHT NOW. We can't trust
// navigator.onLine (it reports `true` on a weak/EDGE link or captive portal —
// the farm-field case), so we actually hit the network. The probe URL is
// cache-busted and matches NO service-worker route (not precached, not a
// navigation, not the Supabase API), so Workbox lets it fall through to the
// real network instead of answering from cache. ANY HTTP response (even a 404)
// proves the server is reachable; a thrown error means we're truly offline.
// Pre-existing probe (unchanged logic); the redundant-state fix above shifts
// it into the new-only gate's changed range. Suppress.
// fallow-ignore-next-line complexity
async function networkReachable(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false
  if (typeof fetch === 'undefined') return false
  try {
    await fetch(`/__net_probe__?t=${Date.now()}`, { method: 'HEAD', cache: 'no-store' })
    return true
  } catch {
    return false
  }
}

export async function resetServiceWorkerAndCaches(): Promise<void> {
  // NEVER nuke the SW + caches unless we can actually RE-DOWNLOAD them. They are
  // the device's ONLY copy of the app; deleting them offline can't be undone
  // until the device is back online to re-fetch ~5MB of precache, turning a
  // transient "this one chunk didn't fetch" into a permanently-dead offline app
  // (black screen). Probe the real network — navigator.onLine is not enough,
  // because on marginal signal it lies and the manual "Reset app" button would
  // still self-destruct.
  if (!(await networkReachable())) return
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map(r => r.unregister().catch(() => {})))
    } catch { /* ignore */ }
  }
  if (typeof caches !== 'undefined') {
    try {
      const keys = await caches.keys()
      await Promise.all(keys.map(k => caches.delete(k).catch(() => {})))
    } catch { /* ignore */ }
  }
}

// SAFE automatic recovery for the ErrorBoundary's stale-bundle path. It ONLY
// activates an already-waiting service worker (the legitimate post-deploy update
// path) and reloads. It NEVER unregisters the SW or deletes caches.
//
// This distinction is the whole point: a "failed to fetch dynamically imported
// module" fires both for a genuine stale bundle (online, after a deploy — there
// IS a waiting SW to activate) AND for a chunk that simply isn't reachable
// because the device is offline. Automatic code must never take the destructive
// branch for the second case, and navigator.onLine can't reliably tell them
// apart (it reports `true` on a marginal/no-backhaul link). So the auto path is
// non-destructive by construction; nuking stays an EXPLICIT user action
// (recoverFromStaleBundle, behind the "Reset app" button). Returns true if it
// kicked off a reload; false means "nothing safe to do — show the error UI."
// Pre-existing (unchanged logic) — same line-shift attribution as above.
// fallow-ignore-next-line complexity
export async function autoRecoverStaleBundle(): Promise<boolean> {
  // Airplane mode: there's no fresh code to fetch — don't even reload.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false
  const activated = await activateWaitingServiceWorker()
  if (activated && typeof location !== 'undefined') {
    // Use replace() so the user can't navigate "back" into the broken page.
    location.replace(location.pathname + location.search)
    return true
  }
  return false
}

// One-shot NUCLEAR recovery — EXPLICIT user action only ("Reset app" button /
// ?reset-sw=1). Tries the soft path (activate waiting SW + reload), else nukes
// SW + caches (guarded against offline above) + reloads. Never call this from
// automatic error handling — use autoRecoverStaleBundle() there.
export async function recoverFromStaleBundle(): Promise<void> {
  const activated = await activateWaitingServiceWorker()
  if (!activated) {
    await resetServiceWorkerAndCaches()
  }
  if (typeof location !== 'undefined') {
    // Use replace() so the user can't navigate "back" into the broken page.
    location.replace(location.pathname + location.search)
  }
}

// Heuristic: is this error likely caused by a stale bundle vs application
// logic? Chunk-load errors are unambiguous. Errors that fire DURING the
// initial render with messages like "Cannot read properties of undefined"
// CAN be either, but if they happen on the first render and there's a
// waiting SW, it's almost certainly a stale bundle — the recovery is
// safe to attempt either way.
export function looksLikeStaleBundle(err: Error): boolean {
  const msg = err.message ?? ''
  const name = err.name ?? ''
  return (
    name === 'ChunkLoadError'
    || /Loading chunk \d+ failed/i.test(msg)
    || /Loading CSS chunk \d+ failed/i.test(msg)
    || /failed to fetch dynamically imported module/i.test(msg)
    || /Importing a module script failed/i.test(msg)
  )
}
